<script setup lang="ts">
import {ref, computed} from "vue";
import {GameMasterCreateChallengeRequest, CharacterStatEnum} from "@/api/dx-backend";
import {GameMasterApi} from "@/api/backendService";

interface Props {
  selectedCharacterId: string;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  close: [];
  challengeCreated: [challengeId: string];
}>();

// Form state
const difficulty = ref<number>(10);
const diceSides = ref<number>(20);
const selectedStat = ref<CharacterStatEnum | ''>('');
const advantage = ref<boolean>(false);
const disadvantage = ref<boolean>(false);
const description = ref<string>('');
const isLoading = ref<boolean>(false);
const error = ref<string>('');

// Available stats from the enum
const availableStats = computed(() => [
  {value: CharacterStatEnum.PhysicalStrength, label: 'Physical Strength'},
  {value: CharacterStatEnum.MentalStrength, label: 'Mental Strength'},
  {value: CharacterStatEnum.FlowResonance, label: 'Flow Resonance'},
  {value: CharacterStatEnum.Concentration, label: 'Concentration'},
  {value: CharacterStatEnum.FlowManipulation, label: 'Flow Manipulation'},
  {value: CharacterStatEnum.FlowConnection, label: 'Flow Connection'},
  {value: CharacterStatEnum.Knowledge, label: 'Knowledge'},
  {value: CharacterStatEnum.Speed, label: 'Speed'},
  {value: CharacterStatEnum.Luck, label: 'Luck'},
  {value: CharacterStatEnum.Charisma, label: 'Charisma'}
]);

// Form validation
const isFormValid = computed(() => {
  return props.selectedCharacterId &&
      difficulty.value > 0 &&
      diceSides.value > 0 &&
      selectedStat.value !== '';
});

// Create challenge
const createChallenge = async () => {
  if (!isFormValid.value) {
    error.value = 'Please fill in all required fields';
    return;
  }

  isLoading.value = true;
  error.value = '';

  try {
    const api = GameMasterApi;
    const challengeRequest: GameMasterCreateChallengeRequest = {
      target_character: props.selectedCharacterId,
      difficulty: difficulty.value,
      dice_sides: diceSides.value,
      stat: selectedStat.value as CharacterStatEnum,
      advantage: advantage.value,
      disadvantage: disadvantage.value,
      description: description.value || undefined,
      modifiers: [] // Empty list as specified
    };

    const response = await api.gamemasterChallengesCreateCreate(challengeRequest);
    emit('challengeCreated', response.data.id || 'challenge-created');
    resetForm();
  } catch (err: any) {
    error.value = err.response?.data?.message || 'Failed to create challenge';
  } finally {
    isLoading.value = false;
  }
};

// Reset form
const resetForm = () => {
  difficulty.value = 10;
  diceSides.value = 20;
  selectedStat.value = '';
  advantage.value = false;
  disadvantage.value = false;
  description.value = '';
  error.value = '';
};

// Clear error
const clearError = () => {
  error.value = '';
};
</script>

<template>
  <div class="gm-panel gm-panel--raised dice-challenge-container">
    <div class="header">
      <div class="header-top">
        <h2 class="gm-title title">Create Dice Challenge</h2>
        <button @click="emit('close')" class="gm-btn gm-btn--ghost gm-btn--sm close-btn" title="Close Challenge Creator">
          ×
        </button>
      </div>
    </div>

    <div class="form-wrapper gm-scroll">
      <form @submit.prevent="createChallenge" class="challenge-form">
        <!-- Error Display -->
        <div v-if="error" class="error-message">
          <span>{{ error }}</span>
          <button type="button" @click="clearError" class="error-close">×</button>
        </div>

        <!-- Target Character Display -->
        <div class="gm-field">
          <label class="gm-label">Target Character</label>
          <div class="gm-input character-display">
            {{ selectedCharacterId.slice(0, 8) }}...
          </div>
        </div>

        <!-- Difficulty -->
        <div class="gm-field">
          <label for="difficulty" class="gm-label">Difficulty Class (DC) *</label>
          <input
              id="difficulty"
              v-model.number="difficulty"
              type="number"
              min="1"
              max="30"
              class="gm-input"
              placeholder="Enter DC (1-30)"
              required
          />
        </div>

        <!-- Dice Sides -->
        <div class="gm-field">
          <label for="diceSides" class="gm-label">Dice Sides</label>
          <input
              id="diceSides"
              v-model.number="diceSides"
              type="number"
              min="4"
              max="100"
              class="gm-input"
              placeholder="Default: 20"
          />
        </div>

        <!-- Stat Selection -->
        <div class="gm-field">
          <label for="stat" class="gm-label">Character Stat *</label>
          <select
              id="stat"
              v-model="selectedStat"
              class="gm-select"
              required
          >
            <option value="">Select a stat...</option>
            <option
                v-for="stat in availableStats"
                :key="stat.value"
                :value="stat.value"
            >
              {{ stat.label }}
            </option>
          </select>
        </div>

        <!-- Advantage/Disadvantage -->
        <div class="gm-field checkbox-group">
          <div class="checkbox-wrapper">
            <input
                id="advantage"
                v-model="advantage"
                type="checkbox"
                class="form-checkbox"
                :disabled="disadvantage"
            />
            <label for="advantage" class="checkbox-label">Advantage</label>
          </div>
          <div class="checkbox-wrapper">
            <input
                id="disadvantage"
                v-model="disadvantage"
                type="checkbox"
                class="form-checkbox"
                :disabled="advantage"
            />
            <label for="disadvantage" class="checkbox-label">Disadvantage</label>
          </div>
        </div>

        <!-- Description -->
        <div class="gm-field">
          <label for="description" class="gm-label">Description (Optional)</label>
          <textarea
              id="description"
              v-model="description"
              class="gm-textarea"
              placeholder="Describe the challenge..."
              rows="3"
          ></textarea>
        </div>

        <!-- Submit Button -->
        <div class="form-actions">
          <button
              type="submit"
              class="gm-btn gm-btn--primary submit-btn"
              :disabled="!isFormValid || isLoading"
          >
            <span v-if="isLoading">Creating...</span>
            <span v-else>Create Challenge</span>
          </button>
          <button
              type="button"
              @click="resetForm"
              class="gm-btn gm-btn--ghost reset-btn"
              :disabled="isLoading"
          >
            Reset
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<style scoped>
.dice-challenge-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 25rem;
  max-height: 37.5rem;
}

.header {
  flex-shrink: 0;
  margin-bottom: var(--gm-gap-sm);
}

.header-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--gm-gap-sm);
  position: relative;
}

.title {
  flex: 1;
  text-align: center;
}

.close-btn {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  padding: 0;
}

.form-wrapper {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: var(--gm-gap-xs);
}

.challenge-form {
  display: flex;
  flex-direction: column;
  gap: var(--gm-gap-lg);
  padding: var(--gm-gap-xs);
}

.error-message {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--gm-gap-sm);
  background: rgba(201, 69, 63, 0.12);
  border: 1px solid rgba(201, 69, 63, 0.4);
  border-radius: var(--gm-radius-sm);
  color: var(--gm-danger);
  font-size: 0.875rem;
}

.error-close {
  background: none;
  border: none;
  color: var(--gm-danger);
  font-size: 1.2rem;
  cursor: pointer;
  padding: 0;
  width: 1.5rem;
  height: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.character-display {
  color: var(--gm-text-muted);
}

.checkbox-group {
  flex-direction: row;
  gap: var(--gm-gap-lg);
  flex-wrap: wrap;
}

.checkbox-wrapper {
  display: flex;
  align-items: center;
  gap: var(--gm-gap-xs);
}

.form-checkbox {
  width: 1rem;
  height: 1rem;
  accent-color: var(--gm-accent);
}

.checkbox-label {
  font-size: 0.85rem;
  font-weight: 400;
  color: var(--gm-text);
  cursor: pointer;
}

.form-actions {
  display: flex;
  gap: var(--gm-gap-sm);
  margin-top: var(--gm-gap-sm);
}

.submit-btn,
.reset-btn {
  flex: 1;
}

@media (max-width: 40rem) {
  .checkbox-group {
    flex-direction: column;
    gap: var(--gm-gap-sm);
  }

  .form-actions {
    flex-direction: column;
  }
}
</style>
